import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Layout, Plus, Search } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
  Textarea,
  type ColumnDef,
} from '@/components/ui'
import { useToast } from '@/components/ui'

interface TemplateRow {
  id: string
  name: string
  description: string | null
  category: string | null
  scope: 'base' | 'industry' | 'customer'
  status: 'draft' | 'active' | 'archived'
  version: number
  step_count: number
  created_at: string
}

const SCOPE_LABELS: Record<string, string> = {
  base: 'Basis',
  industry: 'Branche',
  customer: 'Kunde',
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Entwurf' },
  { value: 'active', label: 'Aktiv' },
  { value: 'archived', label: 'Archiviert' },
]

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  archived: 'Archiviert',
}

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE')
}

export function TemplateListPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { toast } = useToast()
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [creating, setCreating] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [archiveId, setArchiveId] = useState<string | null>(null)

  async function seedPilot() {
    setSeeding(true)
    try {
      const { data } = await apiClient.post<{
        onboarding: { created: boolean }
        offboarding: { created: boolean }
        portalOnboarding: { created: boolean }
      }>('/prozesse/templates/seed-pilot', {})
      const created = [data.onboarding.created, data.offboarding.created, data.portalOnboarding.created].filter(Boolean).length
      qc.invalidateQueries({ queryKey: ['prozesse-templates'] })
      toast({
        title: created > 0 ? `${created} Pilot-Vorlage(n) erstellt` : 'Pilot-Vorlagen existieren bereits',
        tone: 'success',
      })
    } catch {
      toast({ title: 'Pilot-Vorlagen konnten nicht erstellt werden', tone: 'danger' })
    } finally {
      setSeeding(false)
    }
  }

  const q = searchInput

  const { data, isLoading, error } = useQuery<TemplateRow[]>({
    queryKey: ['prozesse-templates', { status: statusFilter, q }],
    queryFn: () =>
      apiClient
        .get<TemplateRow[]>('/prozesse/templates', {
          params: { status: statusFilter || undefined, q: q || undefined },
        })
        .then((r) => r.data),
  })

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const { data: res } = await apiClient.post<{ id: string }>('/prozesse/templates', {
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        scope: 'base',
        status: 'draft',
      })
      qc.invalidateQueries({ queryKey: ['prozesse-templates'] })
      setCreateOpen(false)
      setNewName('')
      setNewDesc('')
      navigate(`/prozesse/templates/${res.id}/designer`)
    } catch {
      toast({ title: 'Vorlage konnte nicht erstellt werden', tone: 'danger' })
    } finally {
      setCreating(false)
    }
  }

  async function handleArchive(id: string) {
    setArchiveId(null)
    try {
      await apiClient.delete(`/prozesse/templates/${id}`)
      qc.invalidateQueries({ queryKey: ['prozesse-templates'] })
      toast({ title: 'Vorlage archiviert', tone: 'success' })
    } catch {
      toast({ title: 'Archivierung fehlgeschlagen', tone: 'danger' })
    }
  }

  const activeFilters = [
    statusFilter && { key: 'status', label: STATUS_LABELS[statusFilter] ?? statusFilter },
    q && { key: 'q', label: `Suche: ${q}` },
  ].filter(Boolean) as { key: string; label: string }[]

  const columns: ColumnDef<TemplateRow, unknown>[] = [
    {
      id: 'name',
      header: 'Vorlage',
      accessorFn: (r) => r.name,
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-neutral-900">{row.original.name}</div>
          {row.original.description && (
            <div className="text-xs text-neutral-500 truncate max-w-xs">
              {row.original.description}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (r) => r.status,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: 'scope',
      header: 'Geltungsbereich',
      accessorFn: (r) => r.scope,
      cell: ({ row }) => (
        <Badge variant="neutral">{SCOPE_LABELS[row.original.scope] ?? row.original.scope}</Badge>
      ),
    },
    {
      id: 'category',
      header: 'Kategorie',
      accessorFn: (r) => r.category ?? '–',
      cell: ({ getValue }) => (
        <span className="text-neutral-600 text-sm">{getValue() as string}</span>
      ),
    },
    {
      id: 'step_count',
      header: 'Schritte',
      accessorFn: (r) => r.step_count,
      cell: ({ getValue }) => (
        <span className="text-neutral-600 text-sm tabular-nums">{getValue() as number}</span>
      ),
    },
    {
      id: 'created_at',
      header: 'Erstellt',
      accessorFn: (r) => r.created_at,
      cell: ({ row }) => (
        <span className="text-neutral-500 text-sm">{fmt(row.original.created_at)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="secondary"
            size="xs"
            onClick={() => navigate(`/prozesse/templates/${row.original.id}/designer`)}
          >
            Bearbeiten
          </Button>
          {row.original.status !== 'archived' && (
            <Button
              variant="ghost"
              size="xs"
              className="text-neutral-500"
              onClick={() => setArchiveId(row.original.id)}
            >
              Archivieren
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Prozess-Vorlagen"
        description="Templates für strukturierte Abläufe und Freigabeprozesse"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={seedPilot} loading={seeding}>
              Pilot-Vorlagen erstellen
            </Button>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Neue Vorlage
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Vorlage suchen…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map(({ value, label }) => (
            <Button
              key={value}
              variant={statusFilter === value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setStatusFilter(statusFilter === value ? '' : value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <FilterBar>
          {activeFilters.map(({ key, label }) => (
            <FilterChip
              key={key}
              active
              onRemove={() => {
                if (key === 'status') setStatusFilter('')
                if (key === 'q') setSearchInput('')
              }}
            >
              {label}
            </FilterChip>
          ))}
        </FilterBar>
      )}

      {isLoading && <LoadingState label="Vorlagen werden geladen…" />}
      {error && (
        <ErrorState
          title="Fehler beim Laden"
          description="Vorlagen konnten nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
        />
      )}
      {!isLoading && !error && data?.length === 0 && (
        <EmptyState
          icon={<Layout className="w-8 h-8" />}
          title="Keine Vorlagen"
          description="Erstellen Sie die erste Prozessvorlage."
          action={
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Neue Vorlage
            </Button>
          }
        />
      )}
      {!isLoading && !error && (data?.length ?? 0) > 0 && (
        <DataTable
          columns={columns}
          data={data ?? []}
          onRowClick={(row) => navigate(`/prozesse/templates/${row.id}/designer`)}
          searchable={false}
          pagination
        />
      )}

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setNewName(''); setNewDesc('') }}
        title="Neue Prozessvorlage"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={creating}
              disabled={!newName.trim()}
            >
              Erstellen & Designer öffnen
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="z.B. Neukunden-Onboarding"
            required
            autoFocus
          />
          <Textarea
            label="Beschreibung"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Optionale Beschreibung…"
            rows={3}
          />
        </div>
      </Modal>

      {/* Archive confirmation */}
      <ConfirmDialog
        open={Boolean(archiveId)}
        title="Vorlage archivieren?"
        message="Archivierte Vorlagen können nicht mehr für neue Prozesse verwendet werden."
        confirmLabel="Archivieren"
        variant="danger"
        onConfirm={() => { if (archiveId) handleArchive(archiveId) }}
        onCancel={() => setArchiveId(null)}
      />
    </div>
  )
}
